import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, Chip } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BarcodeIcon from '@mui/icons-material/QrCode2';
import { getProductById, getProductByBarcode } from '../services/productService';
import { trackScan } from '../services/analyticsService';

// ProductLanding handles `/try?p=...` and `/try?barcode=...` URLs (QR/barcode
// scans). Historically it showed a product card with a "ProvaMi" button; now
// it acts as a silent loader that fetches the product and redirects straight
// to /photo-capture. Only the not-found state remains visible.
function ProductLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('p');
  const barcode = searchParams.get('barcode');

  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchMethod, setSearchMethod] = useState('');

  useEffect(() => {
    const goToCapture = (foundProduct, method) => {
      localStorage.setItem('currentProduct', JSON.stringify({
        ...foundProduct,
        imageUrl: foundProduct.imageUrl || foundProduct.immagine_url
      }));
      trackScan(foundProduct.id, foundProduct.name, method);
      navigate(`/photo-capture?p=${foundProduct.id}`, { replace: true });
    };

    const run = async () => {
      try {
        if (barcode) {
          setSearchMethod('barcode');
          const found = await getProductByBarcode(barcode);
          if (found) return goToCapture(found, 'barcode');
        } else if (productId) {
          setSearchMethod('qr');
          const found = await getProductById(productId);
          if (found) return goToCapture(found, 'qr');
        }
        setNotFound(true);
      } catch (error) {
        console.error('Error loading product:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [productId, barcode, navigate]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Caricamento prodotto...</Typography>
        {searchMethod === 'barcode' && (
          <Chip
            icon={<BarcodeIcon />}
            label={`Barcode: ${barcode}`}
            color="primary"
          />
        )}
      </Box>
    );
  }

  // Product not found
  if (notFound) {
    return (
      <Box
        sx={{
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          p: 3
        }}
      >
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 400 }}>
          <Typography variant="h6" gutterBottom>
            Prodotto non trovato
          </Typography>
          <Typography variant="body2">
            {searchMethod === 'barcode'
              ? `Il barcode "${barcode}" non è presente nel nostro database.`
              : 'Il prodotto richiesto non è disponibile.'
            }
          </Typography>
        </Alert>

        <Button
          variant="contained"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => navigate('/scan')}
          sx={{ mb: 2 }}
        >
          Scansiona Altro Prodotto
        </Button>

        <Button
          variant="outlined"
          onClick={() => navigate('/')}
        >
          Torna alla Home
        </Button>

        {searchMethod === 'barcode' && (
          <Box sx={{ mt: 4, p: 2, backgroundColor: 'white', borderRadius: 2, maxWidth: 400 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Nota per lo staff:</strong> Questo prodotto non è ancora stato aggiunto al sistema.
              Usa il pannello admin per aggiungerlo con il barcode <strong>{barcode}</strong>.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Successful path: the useEffect has already navigated away.
  return null;
}

export default ProductLanding;
